'use client';

import { useTranslations } from 'next-intl';

import { makeFallbackAccessor } from '@/i18n/fallback';

import { getLandingCopy, type LandingCopy, type MarketingCopyRoute } from './marketing-copy';

/**
 * Client counterpart of `getLocalizedLandingCopy`.
 *
 * The catalog already contains every one of these strings in all twelve locales
 * (they are generated FROM marketing-copy.ts), but the marketing pages read the
 * TypeScript module directly, so a localised route rendered Spanish chrome around
 * English hero copy. This resolves the same shape from the catalog instead.
 *
 * Structure — hrefs, ids, ordering, and which optional blocks exist — still comes
 * from marketing-copy.ts. Only the human-readable strings are swapped, so the two
 * can never disagree about shape and English stays the single source of truth.
 *
 * `keywords` is deliberately NOT localised: they are SEO terms, and literally
 * translating a keyword list produces terms nobody searches (bible Ch. 5.5).
 * Per-market keyword research is a separate exercise, gated on a locale actually
 * being promoted to indexed.
 *
 * Exists because /pricing is a Client Component and cannot call the server-only
 * `getTranslations()`. Client components read NextIntlClientProvider instead,
 * which the `[slug]` locale layout already supplies — so this needs no request
 * config at all. Mapping is otherwise identical, so the two cannot diverge in
 * shape.
 */
export function useLocalizedLandingCopy(route: MarketingCopyRoute): LandingCopy {
  const base = getLandingCopy(route);
  const t = useTranslations();
  // Falls back to the English source rather than rendering a raw key on a money
  // page — see src/i18n/fallback.ts.
  const s = makeFallbackAccessor(t);

  return {
    meta: {
      title: s(`${route}.meta.title`, base.meta.title),
      description: s(`${route}.meta.description`, base.meta.description),
      keywords: base.meta.keywords,
    },
    hero: {
      heading: s(`${route}.hero.heading`, base.hero.heading),
      subhead: s(`${route}.hero.subhead`, base.hero.subhead),
      bullets: base.hero.bullets?.map((b, i) => s(`${route}.hero.bullets.${i}`, b)),
      primaryCta: {
        ...base.hero.primaryCta,
        label: s(`${route}.hero.primaryCta`, base.hero.primaryCta.label),
      },
      secondaryCta: base.hero.secondaryCta
        ? {
            ...base.hero.secondaryCta,
            label: s(`${route}.hero.secondaryCta`, base.hero.secondaryCta.label),
          }
        : undefined,
    },
    features: base.features?.map((f, i) => ({
      title: s(`${route}.features.${i}.title`, f.title),
      description: s(`${route}.features.${i}.description`, f.description),
    })),
    useCaseCards: base.useCaseCards?.map((card) => ({
      ...card,
      name: s(`${route}.useCaseCards.${card.id}.name`, card.name),
      description: s(`${route}.useCaseCards.${card.id}.description`, card.description),
      features: card.features.map((f, i) =>
        s(`${route}.useCaseCards.${card.id}.features.${i}`, f)
      ),
    })),
  };
}
